import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  effect,
  inject,
  signal,
} from '@angular/core';
import { AuthService } from '../../auth';
import { Router } from '@angular/router';
import {
  UserService,
  makeArrayFromEnum,
  Olympiada_Sub_Type,
} from '@olymp/shared';
import { TranslocoPipe } from '@jsverse/transloco';

interface UserMenuItem {
  label: string;
  icon: string;
  action: string;
  route?: string;
}

@Component({
  selector: 'lib-user',
  imports: [TranslocoPipe],
  templateUrl: './user.html',
  styleUrl: './user.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class User {
  private readonly host = inject(ElementRef<HTMLElement>);
  $authService = inject(AuthService);
  $router = inject(Router);
  userService$ = inject(UserService);

  readonly open = signal(false);
  protected olympiadSubTypeOptions = makeArrayFromEnum(Olympiada_Sub_Type);
  protected isChangingSubType = false;
  protected pendingSubType: number | null = null;
  protected isSubtypeDropdownOpen = signal(false);
  protected selectedSubType: number | null = null;

  constructor() {
    effect(() => {
      const current = this.userService$.user()?.currentOlympiadSubType ?? null;
      if (current === null) {
        this.selectedSubType = null;
      }
    });
  }

  get currentOlympiadSubType() {
    return this.getAvailableSubTypes().find(
      (type) => type.value === this.userService$.user()?.currentOlympiadSubType,
    )?.label;
  }

  // User menu items with routes
  userMenuItems: UserMenuItem[] = [
    {
      label: 'my_profile',
      icon: 'pi pi-user',
      action: 'profile',
      route: '/profile/cabinet',
    },
  ];

  toggle(): void {
    this.open.update((v) => !v);
  }

  close(): void {
    this.open.set(false);
  }

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    if (this.open() && !this.host.nativeElement.contains(event.target)) {
      this.close();
    }
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    this.close();
  }

  handleMenuAction(action: string) {
    const menuItem = this.userMenuItems.find((item) => item.action === action);
    if (menuItem?.route) {
      this.$router.navigate([menuItem.route]);
      this.close();
    }
  }

  handleLogout() {
    this.logout();
    this.close();
  }

  logout() {
    this.$authService.signOut().subscribe((res) => {
      this.$router.navigate(['/auth/login']);
    });
  }

  protected getAvailableSubTypes() {
    const allowed = this.userService$.user()?.olympiadSubTypes ?? [];
    return this.olympiadSubTypeOptions.filter((option) =>
      allowed.includes(option.value as number),
    );
  }

  protected toggleSubtypeDropdown() {
    this.isSubtypeDropdownOpen.update((open) => !open);
  }

  protected closeSubtypeDropdown() {
    this.isSubtypeDropdownOpen.set(false);
  }

  protected handleSubTypeSelect(subType: number) {
    this.selectedSubType = subType;
    this.handleSubTypeChange(subType);
    this.closeSubtypeDropdown();
  }

  protected handleSubTypeChange(subType: number) {
    if (
      this.isChangingSubType ||
      subType === this.userService$.user()?.currentOlympiadSubType
    ) {
      return;
    }

    this.isChangingSubType = true;
    this.pendingSubType = subType;

    this.userService$.changeOlympiadSubType(subType).subscribe({
      next: () => {
        this.isChangingSubType = false;
        this.pendingSubType = null;
        this.selectedSubType = null;
      },
      error: () => {
        this.isChangingSubType = false;
        this.pendingSubType = null;
        this.selectedSubType = null;
      },
    });
  }
}
